/**
 * Riff Tuner — RevenueCat integration
 *
 * Single place where the SDK is touched. Everything else in the app should go
 * through `usePro()` (see src/composables/usePro.js) rather than importing
 * this module directly.
 *
 * Targets @revenuecat/purchases-capacitor 11.3.2 (last release supporting
 * Capacitor 7).
 */

import { toRaw } from 'vue';
import { Capacitor } from '@capacitor/core';
import {
  Purchases,
  LOG_LEVEL,
  PURCHASES_ERROR_CODE,
} from '@revenuecat/purchases-capacitor';

/* ------------------------------------------------------------------ */
/* Configuration                                                       */
/* ------------------------------------------------------------------ */

/**
 * The entitlement that unlocks Pro. Must match the identifier in the
 * RevenueCat dashboard exactly.
 *
 * Consider renaming this to `pro` in the dashboard before launch — it gets
 * baked into client builds and webhook payloads, so changing it later
 * requires an app release.
 */
export const PRO_ENTITLEMENT = 'pro';

/** The offering that contains the Lifetime package. */
export const DEFAULT_OFFERING = 'default';

/**
 * Public SDK keys, per platform.
 *
 * These are *public* keys and are safe to ship in the client bundle — but they
 * are platform-specific. The `test_...` key only works against RevenueCat's
 * Test Store; it will not transact against Apple or Google.
 */
const API_KEYS = {
  ios: import.meta.env.VITE_RC_IOS_KEY ?? 'appl_YOUR_IOS_KEY',
  android: import.meta.env.VITE_RC_ANDROID_KEY ?? 'goog_YOUR_ANDROID_KEY',
  /** Test Store key — used when running in a browser during development. */
  test: 'test_zImJPYwYgUJXOYeZEFhpzkAtLNk',
};

function apiKeyForPlatform() {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return API_KEYS.ios;
  if (platform === 'android') return API_KEYS.android;
  return API_KEYS.test;
}

/* ------------------------------------------------------------------ */
/* Errors                                                              */
/* ------------------------------------------------------------------ */

export class PurchaseCancelledError extends Error {
  constructor() {
    super('Purchase cancelled by the user.');
    this.name = 'PurchaseCancelledError';
  }
}

/**
 * Turns an SDK error into something safe to show a user.
 *
 * The plugin surfaces errors with a `code` (a PURCHASES_ERROR_CODE value,
 * which is a numeric string like "1" or "10"). Cancellation is not a failure
 * and never produces an error message.
 *
 * @param {any} err
 * @returns {string}
 */
export function describePurchaseError(err) {
  switch (err?.code) {
    case PURCHASES_ERROR_CODE.PURCHASE_NOT_ALLOWED_ERROR:
      return 'Purchases are not allowed on this device. Check your device restrictions and try again.';
    case PURCHASES_ERROR_CODE.PAYMENT_PENDING_ERROR:
      return 'Your payment is pending approval. Pro unlocks as soon as it clears.';
    case PURCHASES_ERROR_CODE.PRODUCT_ALREADY_PURCHASED_ERROR:
      return 'You already own Pro. Tap Restore purchases to unlock it here.';
    case PURCHASES_ERROR_CODE.NETWORK_ERROR:
    case PURCHASES_ERROR_CODE.OFFLINE_CONNECTION_ERROR:
      return 'No connection to the store. Check your network and try again.';
    case PURCHASES_ERROR_CODE.STORE_PROBLEM_ERROR:
      return 'The store is not responding right now. Try again in a few minutes.';
    case PURCHASES_ERROR_CODE.RECEIPT_ALREADY_IN_USE_ERROR:
      return 'This purchase is already linked to another account.';
    case PURCHASES_ERROR_CODE.PRODUCT_NOT_AVAILABLE_FOR_PURCHASE_ERROR:
      return 'Pro is not available in your region yet.';
    default:
      return err?.message ?? 'Something went wrong with the purchase. Try again.';
  }
}

function isCancellation(err) {
  return (
    err?.userCancelled === true ||
    err?.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR
  );
}

/* ------------------------------------------------------------------ */
/* Lifecycle                                                           */
/* ------------------------------------------------------------------ */

let configured = false;

/**
 * Configure the SDK. Safe to call more than once — subsequent calls are
 * ignored. Call once during app bootstrap, before mounting.
 *
 * Do NOT pass an appUserID unless you have your own account system. Without
 * one, RevenueCat generates an anonymous ID and links the purchase to the
 * store account, which is what you want for a lifetime unlock with no login.
 */
export async function configureRevenueCat() {
  if (configured) return;

  if (import.meta.env.DEV) {
    await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
  }

  await Purchases.configure({ apiKey: apiKeyForPlatform() });
  configured = true;
}

export function isConfigured() {
  return configured;
}

/* ------------------------------------------------------------------ */
/* Entitlements                                                        */
/* ------------------------------------------------------------------ */

/**
 * True when the customer currently has Pro unlocked.
 * @param {any} info CustomerInfo
 * @returns {boolean}
 */
export function hasPro(info) {
  if (!info) return false;
  return info.entitlements.active[PRO_ENTITLEMENT] !== undefined;
}

export async function fetchCustomerInfo() {
  const { customerInfo } = await Purchases.getCustomerInfo();
  return customerInfo;
}

/**
 * Subscribe to entitlement changes. Fires after purchases, restores, refunds,
 * and background renewals.
 *
 * Note: unlike most Capacitor plugins, this does NOT return a
 * PluginListenerHandle with a .remove() method. It resolves to a callback ID
 * string, which you pass back to removeCustomerInfoUpdateListener.
 *
 * @param {(info: any) => void} cb
 * @returns {Promise<() => Promise<void>>} unsubscribe
 */
export async function onCustomerInfoChange(cb) {
  const listenerToRemove = await Purchases.addCustomerInfoUpdateListener(cb);

  return async () => {
    try {
      await Purchases.removeCustomerInfoUpdateListener({ listenerToRemove });
    } catch (err) {
      console.error('[RevenueCat] could not remove listener', err);
    }
  };
}

/* ------------------------------------------------------------------ */
/* Offerings and purchasing                                            */
/* ------------------------------------------------------------------ */

/**
 * Cache of the current offering as a plain (non-reactive) object.
 *
 * The paywall plugin needs a raw Offering. Keeping an unwrapped copy here
 * means the plugin never receives a Vue proxy.
 */
let rawOffering = null;

export function getRawOffering() {
  return rawOffering;
}

/**
 * Fetch the current offering. Returns null when no offering is configured or
 * no products could be loaded — treat that as "hide the upgrade UI", not as
 * an error to show the user.
 */
export async function fetchCurrentOffering() {
  const { current, all } = await Purchases.getOfferings();
  rawOffering = current ?? all[DEFAULT_OFFERING] ?? null;
  return rawOffering;
}

/** The Lifetime package from an offering, if present. */
export function lifetimePackage(offering) {
  if (!offering) return null;
  return (
    offering.lifetime ??
    offering.availablePackages.find((p) => p.identifier === 'lifetime') ??
    null
  );
}

/**
 * Buy a package.
 *
 * IMPORTANT (Vue): Capacitor plugins cannot receive Vue reactive Proxy
 * objects. If the package came out of a ref or reactive, it must be unwrapped
 * with toRaw first — hence the call below. Skipping this produces a confusing
 * serialization error at the bridge.
 *
 * Throws PurchaseCancelledError when the user backs out, so callers can
 * distinguish "changed their mind" from a real failure.
 */
export async function purchase(pkg) {
  try {
    const { customerInfo } = await Purchases.purchasePackage({
      aPackage: toRaw(pkg),
    });
    return customerInfo;
  } catch (err) {
    if (isCancellation(err)) throw new PurchaseCancelledError();
    throw err;
  }
}

/**
 * Restore purchases. Required by App Store review for any non-consumable —
 * you must expose this somewhere reachable outside the paywall.
 */
export async function restore() {
  const { customerInfo } = await Purchases.restorePurchases();
  return customerInfo;
}
